// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/audit/GovernanceEventTimeline.jsx

import React from "react";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function titleize(value) {
  return String(value || "")
    .replace(/[_.-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function eventContext(event) {
  const eventType = String(event?.event_type || "");
  const category = String(event?.event_category || "");
  const contextMap = {
    governance_events_exported: "Audit export",
    connector_resumed: "Connector authorization",
    connector_paused: "Connector authorization",
    connector_reauthorization_requested: "Connector authorization",
    connector_governance_updated: "Connector governance",
    connector_owner_assigned: "Connector ownership",
    connector_owner_removed: "Connector ownership",
    user_invitation_created: "User access",
    user_invitation_resent: "User access",
    user_invitation_canceled: "User access",
    user_role_assigned: "Role assignment",
    user_role_removed: "Role assignment",
    user_suspended: "User lifecycle",
    user_reactivated: "User lifecycle",
    organization_profile_initialized: "Organization setup",
    organization_profile_updated: "Organization profile",
    organization_preferences_initialized: "Operating defaults",
    organization_preferences_updated: "Operating defaults",
  };

  if (contextMap[eventType]) return contextMap[eventType];
  if (category === "connectors") return "Connector governance";
  if (category === "users") return "User access";
  if (category === "roles") return "Role governance";
  if (category === "audit") return "Audit trail";
  if (category === "organization") return "Organization settings";
  return titleize(category || "Governance");
}

export default function GovernanceEventTimeline({ events = [], selectedEvent, onSelect, displayPolicy }) {
  if (!events.length) {
    return (
      <section className="enterprise-admin-form-card enterprise-admin-governance-timeline">
        <div className="enterprise-admin-empty compact">
          <strong>No governance events match these filters</strong>
          <p>Clear or adjust the filters to review additional administrative activity.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="enterprise-admin-form-card enterprise-admin-governance-timeline">
      <div className="enterprise-admin-governance-timeline-header">
        <div className="enterprise-admin-card-heading">
          <p className="enterprise-admin-eyebrow">Governance Timeline</p>
          <h3>Reviewable administrative ledger</h3>
          <p>Select an event to inspect actor, target, controls, and source traceability.</p>
        </div>
        <span className="enterprise-admin-governance-result-count">
          {events.length} event{events.length === 1 ? "" : "s"}
        </span>
      </div>

      {displayPolicy?.connector_source_duplicates_collapsed ? (
        <div className="enterprise-admin-inline-note">
          <strong>Clean timeline view</strong>
          <span>Duplicate connector source records are collapsed while audit lineage remains preserved.</span>
        </div>
      ) : null}

      <div className="enterprise-admin-governance-event-list">
        {events.map((event) => (
          <button
            key={event.id}
            type="button"
            className={`enterprise-admin-governance-event-card ${selectedEvent?.id === event.id ? "active" : ""}`}
            onClick={() => onSelect?.(event)}
            aria-pressed={selectedEvent?.id === event.id}
          >
            <span className={`enterprise-admin-severity enterprise-admin-severity-${event.severity || "info"}`}>
              {titleize(event.severity || "info")}
            </span>
            <div>
              <strong>{event.event_title || titleize(event.event_type)}</strong>
              <small>{eventContext(event)}</small>
              <p>{event.event_summary || "Administrative governance event."}</p>
            </div>
            <time>{formatDate(event.occurred_at || event.created_at)}</time>
          </button>
        ))}
      </div>
    </section>
  );
}
